import {
  type AttackTechnique,
} from "../../../lib/types";

/**
 * One owner for the ATT&CK technique chip.
 *
 * This existed twice. `ActionsView` rendered it with a
 * `title={`${tactic}: ${name}`}` tooltip and folded `tactic` into that view's
 * search text; `DataFlowView` rendered the same `.attack-techniques` markup
 * with no tooltip at all and indexed only `id` and `name` into its search. The
 * two had already drifted, and the shared CSS class showed the intent to share
 * them was never carried through to a component.
 *
 * The tooltip and the search text are here rather than at the call sites
 * because all three derive from the same three fields. A chip whose displayed
 * tactic could differ from the tactic it is searchable by is the same class of
 * bug as a technique labelled with a tactic ATT&CK has retired: two copies of
 * one fact, free to disagree.
 *
 * The markup is unchanged, and deliberately so: styling is driven entirely by
 * the `.attack-techniques` class, so both call sites render identically to
 * before.
 */

/** The chip's accessible name and hover text. One format, one place. */
export function attackTechniqueTooltip(technique: AttackTechnique): string {
  return `${technique.tactic}: ${technique.name}`;
}

/**
 * The text a technique contributes to its view's search.
 *
 * `id`, `name` and `tactic` together, so an analyst can find an action or a
 * data flow by any of the three. `DataFlowView` previously indexed only `id`
 * and `name`, which meant a flow could not be found by its tactic; unifying
 * this is the one behaviour change in extracting the component, and it is a
 * widening of what search matches, never a narrowing.
 */
export function attackTechniqueSearchText(
  techniques: readonly AttackTechnique[],
): string {
  return techniques
    .map((item) => `${item.id} ${item.name} ${item.tactic}`)
    .join(" ");
}

export function AttackTechniqueChips({
  techniques,
}: {
  techniques: readonly AttackTechnique[];
}) {
  if (techniques.length === 0) return null;
  return (
    <div className="attack-techniques">
      {techniques.map((item) => (
        <a
          href={item.url}
          key={item.id}
          target="_blank"
          rel="noreferrer"
          title={attackTechniqueTooltip(item)}
        >
          <strong>{item.id}</strong>
          <span>{item.name}</span>
        </a>
      ))}
    </div>
  );
}
